import { MCPCHAT_STORAGE_KEY } from '../constants/mcpchat.constants';
import {
  appendCachedConversationMessage,
  clearCachedConversationMessages,
  moveCachedConversationMessages,
  setCachedConversationMessages,
  updateCachedConversationMessage,
} from './mcpchat.cache';

function persistState(state) {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      MCPCHAT_STORAGE_KEY,
      JSON.stringify({
        chatState: state.chatState,
        conversationId: state.conversationId,
        error: state.error,
      })
    );
  } catch (error) {
    // Ignore storage failures.
  }
}

export const mutations = {
  hydrateState(state, persistedState) {
    state.chatState = persistedState?.chatState || state.chatState;
    state.conversationId = persistedState?.conversationId || null;
    state.error = persistedState?.error || null;
    state.isHydrated = true;
  },
  setChatState(state, chatState) {
    state.chatState = chatState;
    persistState(state);
  },
  addMessage(state, message) {
    appendCachedConversationMessage(state.conversationId, message);
    state.conversationVersion += 1;
    persistState(state);
  },
  setConversationId(state, conversationId) {
    if (!state.conversationId && conversationId) {
      moveCachedConversationMessages(null, conversationId);
    }
    state.conversationId = conversationId;
    persistState(state);
  },
  setLoading(state, isLoading) {
    state.isLoading = isLoading;
    if (!isLoading) {
      state.loadingProgressMessages = [];
    }
  },
  setLoadingProgressMessages(state, messages) {
    state.loadingProgressMessages = Array.isArray(messages) ? messages : [];
  },
  resetHistoryPagination(state) {
    state.historyItems = [];
    state.hasMoreHistory = false;
    state.historyNextOffset = 0;
    state.isHistoryLoading = false;
    state.isHistoryLoadingMore = false;
  },
  setHistoryPage(state, { items, reset = false, hasMoreData = false, nextOffset = null }) {
    const normalizedItems = Array.isArray(items) ? items : [];
    const existingIds = new Set(state.historyItems.map((item) => item.conversation_id));

    state.historyItems = reset
      ? normalizedItems
      : [
          ...state.historyItems,
          ...normalizedItems.filter((item) => !existingIds.has(item?.conversation_id)),
        ];
    state.hasMoreHistory = Boolean(hasMoreData);
    state.historyNextOffset =
      Number.isInteger(nextOffset) && nextOffset >= 0 ? nextOffset : state.historyItems.length;
  },
  setHistoryOpen(state, isOpen) {
    state.isHistoryOpen = Boolean(isOpen);
  },
  setHistoryLoading(state, isLoading) {
    state.isHistoryLoading = Boolean(isLoading);
  },
  setHistoryLoadingMore(state, isLoading) {
    state.isHistoryLoadingMore = Boolean(isLoading);
  },
  invalidateHistoryRequests(state) {
    state.historyRequestVersion += 1;
  },
  setError(state, error) {
    state.error = error;
    persistState(state);
  },
  restoreConversation(state, { conversationId, messages }) {
    state.conversationId = conversationId || null;
    setCachedConversationMessages(state.conversationId, messages);
    state.conversationVersion += 1;
    state.error = null;
    state.isHistoryOpen = false;
    state.isHistoryLoadingMore = false;
    persistState(state);
  },
  invalidatePendingRequests(state) {
    state.requestVersion += 1;
    state.isLoading = false;
    state.loadingProgressMessages = [];
  },
  markActionSubmitted(state, { messageId, actionId }) {
    updateCachedConversationMessage(state.conversationId, messageId, (message) => ({
      ...message,
      actions: Array.isArray(message.actions)
        ? message.actions.map((action) =>
            action.id === actionId ? { ...action, submitted: true } : action
          )
        : message.actions,
      blocks: Array.isArray(message.blocks)
        ? message.blocks.map((block) =>
            block.id === actionId ? { ...block, submitted: true } : block
          )
        : message.blocks,
    }));
    state.conversationVersion += 1;
    persistState(state);
  },
  setSuggestionPrompts(state, prompts) {
    state.suggestionPrompts = Array.isArray(prompts) ? prompts : [];
    state.isSuggestionPromptsLoaded = true;
  },
  clearMessages(state) {
    clearCachedConversationMessages(state.conversationId);
    clearCachedConversationMessages(null);
    state.conversationVersion += 1;
    state.conversationId = null;
    state.error = null;
    state.loadingProgressMessages = [];
    state.isHistoryOpen = false;
    state.isHistoryLoadingMore = false;
    state.hasMoreHistory = false;
    state.historyNextOffset = 0;

    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.removeItem(MCPCHAT_STORAGE_KEY);
      } catch (error) {
        // Ignore storage failures.
      }
    }

    persistState(state);
  },
};
